import { PaginatedResponse } from "shared/types";

// Stored/transported values are lowercase; display labels differ (see VENDOR_LABELS).
export type LockboxVendor = "sentrilock" | "codebox" | "igloohome" | "none";

export type LockboxIntegratedVendor = Exclude<LockboxVendor, "none">;

export const CODE_TO_SEND_SLOTS = [1, 2, 3] as const;
export type CodeToSendSlot = (typeof CODE_TO_SEND_SLOTS)[number];

// Machine reason codes from POST /verify — keep in lockstep with the boompay-api
// BoomCRM::Lockbox::Verify REASON_* constants. `null` means verified.
export type VerifyReason =
  | "unrecognized_serial"
  | "invalid_credentials"
  | "no_integration"
  | "vendor_error"
  | "not_applicable";

export interface VerifyResult {
  verified: boolean;
  reason: VerifyReason | null;
  vendor: LockboxVendor | null;
}

// Persisted verification outcome (CRM-926). "not_verified" until the first verify runs.
export type LockboxVerificationStatus = "not_verified" | "verified" | "failed";

export interface LockboxProperty {
  id: number;
  name: string;
  address?: {
    address1?: string;
    address2?: string;
    city?: string;
    state_or_province?: string;
    postal_code?: string;
  };
}

export interface LockboxUnit {
  id: number;
  title: string;
}

export interface LockboxCreatedBy {
  id: number;
  name: string;
  email: string;
}

// Slim account reference embedded on the lockbox entity (`null` when unassigned).
export interface LockboxVendorAccountRef {
  id: number;
  name: string;
}

export interface Lockbox {
  id: number;
  name: string;
  vendor: LockboxVendor | null; // null for rows created before CRM-926
  vendor_account: LockboxVendorAccountRef | null; // assigned named account, null when unassigned
  code: string | null; // vendor code 1 / admin code 1
  code_2: string | null;
  code_3: string | null;
  notes: string | null;
  purchase_date: string | null; // ISO "YYYY-MM-DD"
  last_battery_replacement_date: string | null; // ISO "YYYY-MM-DD"
  verification_status: LockboxVerificationStatus;
  verification_reason: VerifyReason | null; // set only when verification failed
  verified_at: string | null; // ISO timestamp — "last checked"
  code_to_send_slot: CodeToSendSlot | null;
  code_to_send: string | null;
  active_on_demand_code: GeneratedLockboxCode | null; // still-valid on-demand code, if any
  active: boolean;
  property: LockboxProperty | null;
  unit: LockboxUnit | null;
  created_at: string;
  updated_at: string; // read-only, server-managed
  created_by: LockboxCreatedBy | null;
}

export enum SortOrder {
  ASC = "asc",
  DESC = "desc"
}

export interface GetLockboxesParams {
  page?: number;
  per_page?: number;
  search?: string;
  sort_by?: string;
  sort_order?: SortOrder;
  property_id?: number;
  no_property?: boolean;
  allow_no_property?: boolean;
  unit_id?: number;
  available?: boolean;
}

export type GetLockboxesResponse = PaginatedResponse<Lockbox>;

export const SENTRILOCK_CODE_DURATIONS = [30, 60, 120, 240] as const;
export type SentrilockCodeDuration = (typeof SENTRILOCK_CODE_DURATIONS)[number];

export interface GenerateLockboxCodePayload {
  duration_minutes?: SentrilockCodeDuration;
}

export interface GeneratedLockboxCode {
  code: string;
  vendor: LockboxIntegratedVendor;
  valid_from: string;
  valid_until: string;
  cancelable: boolean;
  generated_at: string;
}

export interface LockboxPayload {
  name: string;
  vendor: LockboxVendor;
  code?: string;
  property_id?: number | null;
  unit_id?: number | null;
  code_2?: string | null;
  code_3?: string | null;
  notes?: string | null;
  purchase_date?: string | null;
  last_battery_replacement_date?: string | null;
  vendor_account_id?: number | null;
  code_to_send_slot?: CodeToSendSlot | null;
  active?: boolean;
}
